/**
 * SunlightManager — Three-Light-Rig for automotive / product visualisation.
 *
 * Three lights, three responsibilities:
 *   HemisphereLight  — sky/ground gradient (outdoor fill, no shadow)
 *   Fill DirectionalLight — studio fill to lift shadow side (no shadow)
 *   Key  DirectionalLight — sun position, shadow caster
 *
 * The public API uses elevation/azimuth (degrees) rather than XYZ so that
 * developers think in sun position, not 3-D coordinates.
 *
 *   elevation: 0° = horizon, 90° = directly overhead (zenith)
 *   azimuth:   0° = north (+Z), 90° = east (+X), 180° = south (-Z), 270° = west (-X)
 *
 * Shadow frustum is auto-fitted from the model AABB via fitShadowFrustum() —
 * no manual shadow camera configuration needed.
 *
 * @see docs/ims-engine-api/11-feature-lighting-shadow.md
 */

import * as THREE from 'three';
import type { SunlightConfig, DayNightAnimation } from '../api/types';
import { devInfo } from '../utils/devLog';
import { SUNLIGHT_PRESETS, DEFAULT_SUNLIGHT_PRESET, sunPosition } from './SunlightPresets';
import { lerp, easeInOutCubic } from './materialManagerTypes';

// Presets and sun-position helper live in SunlightPresets.ts
const PRESETS = SUNLIGHT_PRESETS;
const DEFAULT_PRESET = DEFAULT_SUNLIGHT_PRESET;

/** Lerp duration in milliseconds when switching presets. */
const LERP_DURATION_MS = 800;
/**
 * Maximum delta applied per tick for the preset lerp.
 * Prevents the demand-mode idle spike (first useFrame after long idle =
 * full idle duration) from completing the 800ms transition in one frame
 * and making it look like an instant jump.
 * Same guard pattern as AnimationManager and WheelManager.
 */
const MAX_LERP_DELTA_MS = 50; // ~3 frames at 60 fps

// ---------------------------------------------------------------------------
// SunlerpState — internal state for a single light-rig lerp
// ---------------------------------------------------------------------------

interface SunlerpState {
  fromEl: number; fromAz: number;
  fromKeyInt: number; fromKeyColor: THREE.Color;
  fromFillInt: number;
  fromHemiSky: THREE.Color; fromHemiGround: THREE.Color; fromHemiInt: number;
  toEl: number; toAz: number;
  toKeyInt: number; toKeyColor: THREE.Color;
  toFillInt: number;
  toHemiSky: THREE.Color; toHemiGround: THREE.Color; toHemiInt: number;
  elapsed: number;
  duration: number;
}

// ---------------------------------------------------------------------------
// SunlightManager
// ---------------------------------------------------------------------------

export class SunlightManager {
  // --- Lights ---
  readonly hemiLight: THREE.HemisphereLight;
  readonly fillLight: THREE.DirectionalLight;
  readonly keyLight:  THREE.DirectionalLight;

  // --- State ---
  private _elevation = 65;
  private _azimuth   = 30;

  /** Called whenever elevation changes (e.g. day-night tick) — GroundShadowManager hooks in here. */
  onElevationChange?: (elevation: number) => void;
  /**
   * Called when visual state changes and at least one render frame is needed.
   * The R3F component wires this to `invalidate()` for `frameloop="demand"`.
   */
  onNeedsRender?: () => void;

  // --- Animation ---
  private _animation: Required<DayNightAnimation> | null = null;

  // --- Lerp ---
  private _lerp: SunlerpState | null = null;

  // ---------------------------------------------------------------------------

  constructor() {
    // Hemisphere — outdoor sky/ground gradient
    this.hemiLight = new THREE.HemisphereLight('#ffffff', '#444444', 0.2);
    this.hemiLight.name = 'IMS_HemiLight';

    // Fill — studio fill, no shadow
    this.fillLight = new THREE.DirectionalLight('#ffffff', 0.5);
    this.fillLight.castShadow = false;
    this.fillLight.position.set(-5, 8, -3);
    this.fillLight.name = 'IMS_FillLight';

    // Key / Sun — shadow caster
    this.keyLight = new THREE.DirectionalLight('#ffffff', 0.6);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(2048, 2048);
    this.keyLight.shadow.radius = 5; // PCFSoftShadowMap blur radius — higher = softer edges
    this.keyLight.shadow.camera.near = 0.5;
    this.keyLight.shadow.camera.far  = 50;
    (this.keyLight.shadow.camera as THREE.OrthographicCamera).left   = -5;
    (this.keyLight.shadow.camera as THREE.OrthographicCamera).right  =  5;
    (this.keyLight.shadow.camera as THREE.OrthographicCamera).top    =  5;
    (this.keyLight.shadow.camera as THREE.OrthographicCamera).bottom = -5;
    this.keyLight.name = 'IMS_KeyLight';

    // Apply default preset
    this._applyConfig(PRESETS[DEFAULT_PRESET]);
  }

  // ---------------------------------------------------------------------------
  // apply
  // ---------------------------------------------------------------------------

  /**
   * Apply a named preset or direct config.
   *
   * By default, all numeric and colour values lerp smoothly over `LERP_DURATION_MS`
   * so preset-to-preset transitions feel cinematic. Pass `{ animated: false }` to
   * apply values instantly (useful in unit tests or initial setup).
   *
   * @example
   * manager.apply('golden-hour')
   * manager.apply({ elevation: 45, azimuth: 180, intensity: 1.4 })
   * manager.apply('daylight', { animated: false }) // instant
   */
  apply(config: string | SunlightConfig, options: { animated?: boolean } = {}): void {
    const animated = options.animated ?? true;
    let resolved: SunlightConfig;

    if (typeof config === 'string') {
      const preset = PRESETS[config];
      if (!preset) {
        devInfo('sunlightManager.apply()', `Unknown preset "${config}", ignoring.`);
        return;
      }
      resolved = preset;
    } else {
      resolved = config;
    }

    if (animated) {
      this._startLerp(resolved);
    } else {
      this._applyConfig(resolved);
    }
  }

  // ---------------------------------------------------------------------------
  // Shadow frustum auto-fit
  // ---------------------------------------------------------------------------

  /**
   * Fit the shadow camera frustum tightly to the model's AABB.
   * Call this after ModelComponent mounts to eliminate pixelated / clipped shadows
   * without any manual configuration.
   *
   * @param aabb - Axis-aligned bounding box of the loaded model.
   */
  fitShadowFrustum(aabb: THREE.Box3): void {
    const size   = aabb.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) * 0.7;

    const cam = this.keyLight.shadow.camera as THREE.OrthographicCamera;
    cam.left   = -radius;
    cam.right  =  radius;
    cam.top    =  radius;
    cam.bottom = -radius;
    cam.updateProjectionMatrix();
    this.keyLight.shadow.needsUpdate = true;
    this.onNeedsRender?.();
  }

  // ---------------------------------------------------------------------------
  // Day-night animation
  // ---------------------------------------------------------------------------

  /**
   * Start or stop day-night cycle animation.
   * @param animation - Animation config, or null to stop.
   */
  setAnimation(animation: DayNightAnimation | null): void {
    if (animation === null) {
      this._animation = null;
      return;
    }
    this._animation = {
      speed:   animation.speed   ?? 20,
      azimuth: animation.azimuth ?? 200,
    };
  }

  /**
   * Advance the animation by one frame.
   * Must be called from the R3F render loop (useFrame).
   * Handles both the day-night cycle animation and any active preset lerp.
   * @param deltaMs - Time since last frame in milliseconds.
   */
  tick(deltaMs: number): void {
    // ── Day-night animation ──────────────────────────────────────────────────────────────────────────────────
    if (this._animation) {
      const deltaDeg = this._animation.speed * (deltaMs / 1000);
      this._elevation = ((this._elevation + deltaDeg) % 360);
      this._azimuth   = this._animation.azimuth;
      this._updateKeyLightPosition();
      this.onElevationChange?.(this._elevation);
    }

    // ── Preset lerp ──────────────────────────────────────────────────────────────────────────────────────────────────────
    // Clamp lerp delta to MAX_LERP_DELTA_MS: a demand-mode idle spike (tab
    // switch, long pause) means the first useFrame delta equals the full idle
    // duration. Without this cap, the entire 800ms lerp completes in one frame
    // and appears as an instant jump — same fix as WheelManager.
    if (this._lerp) {
      const lerpDt = Math.min(deltaMs, MAX_LERP_DELTA_MS);
      this._lerp.elapsed = Math.min(this._lerp.elapsed + lerpDt, this._lerp.duration);
      const t = easeInOutCubic(this._lerp.elapsed / this._lerp.duration);
      this._applyLerped(this._lerp, t);
      if (this._lerp.elapsed >= this._lerp.duration) {
        // Snap to exact final values
        this._applyLerped(this._lerp, 1);
        this._lerp = null;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  get elevation(): number { return this._elevation; }
  get azimuth():   number { return this._azimuth;   }
  /** True when either the day-night animation or a preset lerp is running. */
  get isAnimating(): boolean { return this._animation !== null || this._lerp !== null; }
  /** True only while a preset-to-preset lerp is in progress. */
  get isLerping(): boolean { return this._lerp !== null; }

  /** Returns preset names available for apply(). */
  static presetNames(): string[] {
    return Object.keys(PRESETS);
  }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    this._animation = null;
    this._lerp      = null;
    this.onElevationChange = undefined;
    // NOTE: onNeedsRender is NOT cleared here — it is owned by the
    // SunlightManagerComponent R3F component and cleaned up via its
    // useEffect return.

    // Dispose shadow map GPU resources
    this.keyLight.shadow.map?.dispose();
    // Dispose light objects
    this.keyLight.dispose();
    this.fillLight.dispose();
    this.hemiLight.dispose();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Start a smooth lerp from the current rig state to the target config.
   * Fields not present in `config` keep their current value (no jump).
   * `castShadow` is binary and applied immediately at lerp start.
   */
  private _startLerp(config: SunlightConfig): void {
    // castShadow is binary — apply immediately, don't interpolate
    if (config.castShadow !== undefined) this.keyLight.castShadow = config.castShadow;

    // If only castShadow was specified (no lerpable fields), preserve any
    // in-progress preset transition — toggling a boolean shouldn't restart
    // a colour / position interpolation.
    const hasLerpable = config.elevation !== undefined || config.azimuth !== undefined ||
      config.intensity !== undefined || config.color !== undefined ||
      config.fillIntensity !== undefined || config.hemi !== undefined;
    if (!hasLerpable) {
      this.onNeedsRender?.();
      return;
    }

    this._lerp = {
      // ── FROM: current live values ──────────────────────────────────────────
      fromEl:         this._elevation,
      fromAz:         this._azimuth,
      fromKeyInt:     this.keyLight.intensity,
      fromKeyColor:   this.keyLight.color.clone(),
      fromFillInt:    this.fillLight.intensity,
      fromHemiSky:    this.hemiLight.color.clone(),
      fromHemiGround: this.hemiLight.groundColor.clone(),
      fromHemiInt:    this.hemiLight.intensity,
      // ── TO: target values (fall back to current if not specified) ───────────
      // Use .clone() instead of getHexString() to avoid missing '#' prefix
      // which would cause THREE.Color to fall back to white.
      toEl:         config.elevation     ?? this._elevation,
      toAz:         config.azimuth       ?? this._azimuth,
      toKeyInt:     config.intensity     ?? this.keyLight.intensity,
      toKeyColor:   config.color         ? new THREE.Color(config.color) : this.keyLight.color.clone(),
      toFillInt:    config.fillIntensity ?? this.fillLight.intensity,
      toHemiSky:    config.hemi?.sky     ? new THREE.Color(config.hemi.sky)    : this.hemiLight.color.clone(),
      toHemiGround: config.hemi?.ground  ? new THREE.Color(config.hemi.ground) : this.hemiLight.groundColor.clone(),
      toHemiInt:    config.hemi?.intensity ?? this.hemiLight.intensity,
      elapsed:  0,
      duration: LERP_DURATION_MS,
    };

    // Kick demand-mode: call invalidate for the first frame
    this.onNeedsRender?.();
  }

  /**
   * Write interpolated values at parameter `t` (0 = from, 1 = to) to the lights.
   */
  private _applyLerped(s: SunlerpState, t: number): void {
    this._elevation = lerp(s.fromEl, s.toEl, t);
    this._azimuth   = lerp(s.fromAz, s.toAz, t);

    this.keyLight.intensity = lerp(s.fromKeyInt, s.toKeyInt, t);
    this.keyLight.color.lerpColors(s.fromKeyColor, s.toKeyColor, t);

    this.fillLight.intensity = lerp(s.fromFillInt, s.toFillInt, t);

    this.hemiLight.intensity = lerp(s.fromHemiInt, s.toHemiInt, t);
    this.hemiLight.color.lerpColors(s.fromHemiSky, s.toHemiSky, t);
    this.hemiLight.groundColor.lerpColors(s.fromHemiGround, s.toHemiGround, t);

    this._updateKeyLightPosition();
    this.onElevationChange?.(this._elevation);
  }

  private _applyConfig(config: SunlightConfig): void {
    if (config.elevation !== undefined) this._elevation = config.elevation;
    if (config.azimuth   !== undefined) this._azimuth   = config.azimuth;

    // Key light
    if (config.intensity   !== undefined) this.keyLight.intensity = config.intensity;
    if (config.color       !== undefined) this.keyLight.color.set(config.color);
    if (config.castShadow  !== undefined) this.keyLight.castShadow = config.castShadow;
    this._updateKeyLightPosition();

    // Fill light
    if (config.fillIntensity !== undefined) this.fillLight.intensity = config.fillIntensity;

    // Hemi light
    if (config.hemi) {
      const h = config.hemi;
      if (h.sky       !== undefined) this.hemiLight.color.set(h.sky);
      if (h.ground    !== undefined) this.hemiLight.groundColor.set(h.ground);
      if (h.intensity !== undefined) this.hemiLight.intensity = h.intensity;
    }

    // Notify elevation change (e.g. GroundShadowManager adjusts shadowLength)
    this.onElevationChange?.(this._elevation);
    this.onNeedsRender?.();
  }

  private _updateKeyLightPosition(): void {
    const pos = sunPosition(this._elevation, this._azimuth);
    this.keyLight.position.copy(pos);
    this.keyLight.shadow.needsUpdate = true;
  }
}
